import { create } from 'zustand'

import {
  authWithPassword,
  logout as apiLogout,
  refreshAuth,
  requestPasswordReset,
  confirmPasswordReset,
  updateCurrentUser,
  updateNotificationPreferences,
  changePassword as apiChangePassword,
} from '@/core/api/usersService'
import { authStore } from '@/core/api/client'
import type { NotificationPreferences, PBUser } from '@/core/api/usersService'

export type UserRole = 'admin' | 'instructor' | 'staff' | 'company_admin' | 'student'

export interface AuthState {
  user: PBUser | null
  isAuthenticated: boolean
  /** true while we are checking an existing session on mount */
  isCheckingAuth: boolean
  /** true while a login request is in-flight */
  isLoggingIn: boolean
  loginError: string | null
  /** password reset state */
  isRequestingReset: boolean
  resetRequestError: string | null
  resetRequestSuccess: string | null
  isConfirmingReset: boolean
  resetConfirmError: string | null
  resetConfirmSuccess: string | null

  /** Called once on app mount to restore an existing session via authRefresh */
  checkAuth: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  requestReset: (email: string) => Promise<void>
  confirmReset: (code: string, newPassword: string) => Promise<void>
  updateUser: (data: { name?: string; phone?: string; email?: string }) => Promise<void>
  updateNotificationPreferences: (prefs: NotificationPreferences) => Promise<void>
  changePassword: (data: { currentPassword: string; newPassword: string }) => Promise<void>
  clearResetMessages: () => void
  setUser: (user: PBUser) => void

  // Role helpers
  isAdmin: boolean
  isInstructor: () => boolean
  isStaff: () => boolean
  isCompanyAdmin: () => boolean
  isStudent: () => boolean
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  isCheckingAuth: true,
  isLoggingIn: false,
  loginError: null,
  isRequestingReset: false,
  resetRequestError: null,
  resetRequestSuccess: null,
  isConfirmingReset: false,
  resetConfirmError: null,
  resetConfirmSuccess: null,

  checkAuth: async () => {
    if (!authStore.isValid) {
      set({ isAuthenticated: false, user: null, isAdmin: false, isCheckingAuth: false })
      return
    }
    try {
      const user = await refreshAuth()
      set({ isAuthenticated: true, user, isAdmin: user?.role === 'admin', isCheckingAuth: false })
    } catch {
      set({ isAuthenticated: false, user: null, isAdmin: false, isCheckingAuth: false })
    }
  },

  login: async (email, password) => {
    set({ isLoggingIn: true, loginError: null })
    try {
      const user = await authWithPassword(email, password)
      set({ isAuthenticated: true, user, isAdmin: user?.role === 'admin', isLoggingIn: false })
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Incorrect credentials. Please try again.'
      set({ loginError: msg, isLoggingIn: false })
    }
  },

  logout: () => {
    apiLogout()
    set({ isAuthenticated: false, user: null, isAdmin: false })
  },

  requestReset: async (email) => {
    set({ isRequestingReset: true, resetRequestError: null, resetRequestSuccess: null })
    try {
      const result = await requestPasswordReset(email)
      set({ resetRequestSuccess: result.message, isRequestingReset: false })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send reset email.'
      set({ resetRequestError: msg, isRequestingReset: false })
    }
  },

  confirmReset: async (code, newPassword) => {
    set({ isConfirmingReset: true, resetConfirmError: null, resetConfirmSuccess: null })
    try {
      const result = await confirmPasswordReset(code, newPassword)
      set({ resetConfirmSuccess: result.message, isConfirmingReset: false })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to reset password.'
      set({ resetConfirmError: msg, isConfirmingReset: false })
    }
  },

  updateUser: async (data) => {
    const user = await updateCurrentUser(data)
    set({ user, isAdmin: user?.role === 'admin' })
  },

  updateNotificationPreferences: async (prefs) => {
    const updated = await updateNotificationPreferences(prefs)
    const current = get().user
    if (current) {
      set({ user: { ...current, notificationPreferences: updated } })
    }
  },

  changePassword: async (data) => {
    await apiChangePassword(data)
  },

  clearResetMessages: () => {
    set({
      resetRequestError: null,
      resetRequestSuccess: null,
      resetConfirmError: null,
      resetConfirmSuccess: null,
    })
  },

  setUser: (user) => {
    set({ user, isAdmin: user.role === 'admin' })
  },

  // Role helpers — match backend permission logic:
  // admin is a superset of instructor, staff, and company_admin
  isInstructor: () => {
    const role = get().user?.role
    return role === 'admin' || role === 'instructor'
  },
  isStaff: () => {
    const role = get().user?.role
    return role === 'admin' || role === 'staff'
  },
  isCompanyAdmin: () => {
    const role = get().user?.role
    return role === 'admin' || role === 'company_admin'
  },
  isStudent: () => get().user?.role === 'student',
}))
